import { useEffect, useState } from 'react';
import { authClient } from '@frontend/api-clients/authClient';
import { listAiProfiles } from '@frontend/api-clients/aiProfilesClient';
import type { WidgetProps } from '@frontend/widgets/types';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface StoryDraft {
	subject: string;
	description?: string;
	acceptanceCriteria?: string[];
	storyPoints?: number;
	priority?: string;
}

interface EpicDraft {
	subject: string;
	description?: string;
	acceptanceCriteria?: string[];
}

interface DraftResponse {
	draftId: string;
	epic: EpicDraft;
	stories: StoryDraft[];
	projectIdentifier?: string | null;
}

interface CreateResult {
	epicId: number | null;
	storyIds: number[];
	createdCount: number;
	openProjectBaseUrl: string;
	epicUrl: string | null;
}

type Stage = 'idle' | 'generating' | 'reviewing' | 'creating' | 'done' | 'error';

// ---------------------------------------------------------------------------
// API helpers
// ---------------------------------------------------------------------------

async function postJson<T>(path: string, body: unknown): Promise<T> {
	const token = authClient.getToken();
	const res = await fetch(path, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
			...(token ? { Authorization: `Bearer ${token}` } : {}),
		},
		body: JSON.stringify(body),
	});
	const data = (await res.json()) as { message?: string } & T;
	if (!res.ok) throw new Error((data as { message?: string }).message || `Request failed (${res.status})`);
	return data;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function StoryCard({
	story,
	index,
	onChange,
}: {
	story: StoryDraft;
	index: number;
	onChange: (i: number, updated: StoryDraft) => void;
}) {
	return (
		<div className="ai-story-card">
			<div className="ai-story-card-header">
				<span className="ai-story-index">Story {index + 1}</span>
				{story.storyPoints !== undefined && (
					<span className="ai-story-points">{story.storyPoints} pts</span>
				)}
				{story.priority && (
					<span className={`ai-story-priority ai-priority-${story.priority}`}>{story.priority}</span>
				)}
			</div>
			<input
				className="ai-story-subject"
				value={story.subject}
				onChange={(e) => onChange(index, { ...story, subject: e.target.value })}
				placeholder="Story subject…"
			/>
			{story.description && <p className="ai-story-description">{story.description}</p>}
			{story.acceptanceCriteria && story.acceptanceCriteria.length > 0 && (
				<ul className="ai-story-ac">
					{story.acceptanceCriteria.map((ac, i) => (
						<li key={i}>{ac}</li>
					))}
				</ul>
			)}
		</div>
	);
}

// ---------------------------------------------------------------------------
// Main widget
// ---------------------------------------------------------------------------

export default function AIStoryWriterWidget({ contextType, contextId, ecosystemId }: WidgetProps) {
	const projectId = contextType === 'project' ? contextId : '';
	const [stage, setStage] = useState<Stage>('idle');
	const [brief, setBrief] = useState('');
	const [draft, setDraft] = useState<DraftResponse | null>(null);
	const [editableStories, setEditableStories] = useState<StoryDraft[]>([]);
	const [result, setResult] = useState<CreateResult | null>(null);
	const [errorMsg, setErrorMsg] = useState('');
	const [hasProfile, setHasProfile] = useState<boolean | null>(null);
	const [activeProfileName, setActiveProfileName] = useState('');

	useEffect(() => {
		if (!projectId) {
			setHasProfile(false);
			return;
		}
		listAiProfiles({ ecosystemId, projectId })
			.then((data) => {
				if (!data.available) {
					// Legacy AI_PROVIDER mode may still work server-side.
					setHasProfile(true);
					setActiveProfileName('Server default');
					return;
				}
				const selected = data.profiles.find((p) => p.id === data.selectedProfileId) || null;
				setHasProfile(Boolean(selected));
				setActiveProfileName(selected?.name || '');
			})
			.catch(() => {
				setHasProfile(true);
			});
	}, [ecosystemId, projectId]);

	const handleGenerate = async () => {
		if (!brief.trim() || !projectId) return;
		setStage('generating');
		setErrorMsg('');
		try {
			const response = await postJson<DraftResponse>('/api/ai/story-drafts', {
				brief: brief.trim(),
				ecosystemId,
				projectId,
			});
			setDraft(response);
			setEditableStories(response.stories);
			setStage('reviewing');
		} catch (err) {
			setErrorMsg((err as Error).message);
			setStage('error');
		}
	};

	const handleApprove = async () => {
		if (!draft || !projectId) return;
		setStage('creating');
		setErrorMsg('');
		try {
			const response = await postJson<CreateResult>('/api/ai/openproject/work-packages', {
				draftId: draft.draftId,
				epic: draft.epic,
				stories: editableStories,
				projectId,
				ecosystemId,
			});
			setResult(response);
			setStage('done');
		} catch (err) {
			setErrorMsg((err as Error).message);
			setStage('error');
		}
	};

	const handleReset = () => {
		setStage('idle');
		setBrief('');
		setDraft(null);
		setEditableStories([]);
		setResult(null);
		setErrorMsg('');
	};

	if (!projectId) {
		return (
			<div className="widget-inner-empty">
				Open a project to generate AI stories for its OpenProject backlog.
			</div>
		);
	}

	if (hasProfile === false) {
		return (
			<div className="ai-story-writer">
				<div className="widget-inner-empty">
					Configure an AI connection with the gear icon before generating stories.
				</div>
			</div>
		);
	}

	// --- Idle ---
	if (stage === 'idle') {
		return (
			<div className="ai-story-writer">
				<p className="ai-story-intro">
					Describe your initiative in plain language. The AI will draft an epic and supporting stories
					for review before anything is created in OpenProject.
				</p>
				{activeProfileName ? (
					<p className="tree-dashboard-muted">Using connection: {activeProfileName}</p>
				) : null}
				<textarea
					className="ai-brief-input"
					rows={5}
					placeholder="Example: We need a user authentication module that supports SSO via Keycloak…"
					value={brief}
					onChange={(e) => setBrief(e.target.value)}
					maxLength={4000}
				/>
				<div className="ai-brief-meta">
					<span className="ai-char-count">{brief.length}/4000</span>
				</div>
				<button
					className="btn btn-primary ai-generate-btn"
					onClick={handleGenerate}
					disabled={!brief.trim()}
				>
					Generate Draft
				</button>
			</div>
		);
	}

	if (stage === 'generating') {
		return (
			<div className="ai-story-writer ai-story-loading">
				<div className="widget-inner-loading">Generating draft…</div>
				<p className="ai-story-hint">This may take 10–30 seconds.</p>
			</div>
		);
	}

	if (stage === 'error') {
		return (
			<div className="ai-story-writer">
				<div className="widget-inner-error">{errorMsg}</div>
				<button className="btn ai-back-btn" onClick={handleReset}>
					← Start over
				</button>
			</div>
		);
	}

	if (stage === 'done' && result) {
		return (
			<div className="ai-story-writer ai-story-done">
				<div className="ai-success-header">
					<span className="ai-success-icon">✓</span>
					<span>
						Created {result.createdCount} stor{result.createdCount === 1 ? 'y' : 'ies'} + 1 epic in
						OpenProject
					</span>
				</div>
				{result.epicUrl && (
					<a className="ai-epic-link" href={result.epicUrl} target="_blank" rel="noopener noreferrer">
						Open epic in OpenProject →
					</a>
				)}
				<button className="btn ai-back-btn" onClick={handleReset}>
					Write another
				</button>
			</div>
		);
	}

	if (stage === 'reviewing' && draft) {
		return (
			<div className="ai-story-writer ai-story-review">
				<div className="ai-review-header">
					<h4 className="ai-review-title">Review Draft</h4>
					<span className="ai-review-hint">Edit story subjects before approving.</span>
				</div>

				<div className="ai-epic-block">
					<div className="ai-epic-label">Epic</div>
					<div className="ai-epic-subject">{draft.epic.subject}</div>
					{draft.epic.description && <p className="ai-epic-description">{draft.epic.description}</p>}
					{draft.epic.acceptanceCriteria && draft.epic.acceptanceCriteria.length > 0 && (
						<ul className="ai-story-ac">
							{draft.epic.acceptanceCriteria.map((ac, i) => (
								<li key={i}>{ac}</li>
							))}
						</ul>
					)}
				</div>

				<div className="ai-stories-block">
					{editableStories.map((story, i) => (
						<StoryCard
							key={i}
							story={story}
							index={i}
							onChange={(idx, updated) => {
								setEditableStories((prev) => prev.map((s, j) => (j === idx ? updated : s)));
							}}
						/>
					))}
				</div>

				<div className="ai-review-footer">
					{errorMsg && <div className="widget-inner-error">{errorMsg}</div>}
					<div className="ai-review-actions">
						<button className="btn ai-back-btn" onClick={handleReset}>
							← Discard
						</button>
						<button className="btn btn-primary ai-approve-btn" onClick={handleApprove}>
							Approve &amp; Create in OpenProject
						</button>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="ai-story-writer ai-story-loading">
			<div className="widget-inner-loading">Creating work packages in OpenProject…</div>
		</div>
	);
}
